import type { QueryClient } from "@tanstack/react-query";
import { ApiError, createVenue, startSelfServeTrial } from "@repo/api-client";
import { slugCandidates } from "./venueSlug";

/**
 * A slug collision, as opposed to any other failure. Both "already taken" and "on the reserved
 * blocklist" come back as 409 Conflict, and a different candidate resolves either — whereas a
 * validation, entitlement or network failure will not be fixed by trying another name.
 */
function isSlugConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * Create an account's first venue from the restaurant's name, returning its id — or `null` if it
 * could not be created.
 *
 * Shared by the two places an account can end up owning no venue: straight after signup, and the
 * picker's empty state (a pre-existing account, or one whose only venue was offboarded). They are
 * the same operation, and having one copy is what stops the two drifting on slug rules.
 *
 * NEVER THROWS, by design. Both callers reach it after something else has already succeeded — an
 * account exists either way — so a failure here must degrade to "no venue yet", never to an error
 * claiming the earlier step failed.
 *
 * Requires a token carrying `tenant_id` and ACCOUNT_ROOT: `POST /venues` is root-gated, and the
 * tenancy bootstrap binds no tenant, so callers must have refreshed their claims first.
 *
 * TAKES THE QUERY CLIENT because refreshing the `["me"]` bootstrap is not optional here, and
 * leaving it to the caller meant it got forgotten. That query has a 60s staleTime and is what
 * VenueShellLayout resolves `/v/:venueId` against — so navigating to a venue created a moment ago
 * hits a cache that has never heard of it, the guard bounces to /select-venue, and the picker
 * renders its EMPTY state from the same stale data. The owner sees the dead end they just escaped,
 * clicks create again, and the second venue is refused by the MULTI_SITE gate. Observed exactly
 * that way on staging.
 */
export async function createFirstVenue(
  name: string,
  queryClient: QueryClient,
): Promise<string | null> {
  // Slugs are claimed platform-wide, first-writer-wins, so a collision on a common restaurant name
  // is expected rather than exceptional — walk the numbered candidates.
  for (const slug of slugCandidates(name)) {
    try {
      const venue = await createVenue({
        slug,
        displayName: name,
        // Not asked for: changeable in settings, and one more question on the way in costs more
        // than it is worth. The onboarding wizard covers the rest of setup.
        category: "RESTAURANT",
      });
      // The generated model types `id` as optional; treat a response without one as a failure to
      // create rather than routing to `/v/undefined/onboarding`.
      if (venue.id === undefined) return null;

      // Start the trial before handing back, because the venue alone does not get the owner
      // anywhere: GoLiveService requires the subscription in {TRIALING, ACTIVE}, and until a trial
      // exists every venue sits at PREVIEW. Activating one drives them to TRIALING, which is the
      // gate. Swallowed on failure and NOT retried with a different slug — the venue exists either
      // way, and an owner with a venue but no trial can still be helped from the billing page,
      // whereas a signup that errors here would look like it failed outright.
      await startSelfServeTrial().catch(() => undefined);
      // AWAITED: the caller navigates straight into the venue, and the guard there reads this
      // cache. Firing and forgetting would reintroduce the bounce this exists to prevent.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      return venue.id;
    } catch (error) {
      // 409 is the slug race — try the next candidate. Anything else (entitlement, validation,
      // network) will not be fixed by a different slug, so stop.
      if (!isSlugConflict(error)) return null;
    }
  }
  return null;
}
